import { FormlyFieldConfig } from '@ngx-formly/core';
import { TripettoNode } from '../utils/tripetto-interfaces';
import { convertMarkdownToHtml } from '../utils/markdown-converter';
import { convertHideExpression } from '../utils/expression-converter';

/**
 * Convert Tripetto address block to Formly mat-formly-address field
 */
export function convertAddressBlock(node: TripettoNode): FormlyFieldConfig {
	const block = node.block;
	const slots = node.slots || [];

	// Find slots by reference
	const findSlot = (ref: string) => slots.find((s) => s.reference === ref);
	const addressSlot = findSlot('address');
	const line1Slot = findSlot('line1');
	const line2Slot = findSlot('line2');
	const citySlot = findSlot('city');
	const stateSlot = findSlot('state');
	const zipSlot = findSlot('zip');
	const countySlot = findSlot('county');

	// Determine key (use first slot's alias if available, otherwise node id)
	const key = addressSlot?.alias || node.id;

	// Build props - mat-formly-address uses these to control visibility and behavior
	// WORKAROUND: mat-formly-address template doesn't render props.name, only props.label
	// See formly-improvements.txt #2
	const props: any = {
		label: block?.label || (node.nameVisible !== false ? convertMarkdownToHtml(node.name) : undefined),
		description: convertMarkdownToHtml(node.description),
		// Visibility props - control which fields are displayed
		showAutoFillAddressSearch: true, // Always show address search by default
		showStreetAddress1: !!line1Slot,
		showStreetAddress2: !!line2Slot,
		showCity: !!citySlot,
		showState: !!stateSlot,
		showZipCode: !!zipSlot,
		showCounty: !!countySlot,
		showCountry: false // Tripetto address doesn't include country by default
	};

	// Build per-field required list from individual slot required flags
	// The address component uses this to apply Validators.required to child controls
	const requiredFields: string[] = [];
	if (line1Slot?.required) requiredFields.push('AddressLine1');
	if (line2Slot?.required) requiredFields.push('AddressLine2');
	if (citySlot?.required) requiredFields.push('City');
	if (stateSlot?.required) requiredFields.push('State');
	if (zipSlot?.required) requiredFields.push('Zip');
	if (countySlot?.required) requiredFields.push('County');

	if (requiredFields.length > 0) {
		props.required = true;
		props.requiredFields = requiredFields;
	}

	// Build the mat-formly-address field configuration
	// Note: mat-formly-address creates its own FormGroup with child controls internally
	const field: FormlyFieldConfig = {
		key,
		type: 'mat-formly-address',
		props
	};

	// Add expressions.hide if there are simple conditions
	const hideExpression = convertHideExpression(node);
	if (hideExpression) {
		field.expressions = { hide: hideExpression };
	}

	return field;
}
